import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar, { MobileDrawer } from './Sidebar';
import Topbar from './Topbar';
import UploadTray from '../documents/UploadTray';
import useAsync from '../../hooks/useAsync';
import adminService from '../../services/adminService';
import folderService from '../../services/folderService';
import { useAuth } from '../../context/AuthContext';
import { P } from '../../utils/permissions';

/**
 * v2 shell: sticky 248px sidebar, 60px top bar, content column. Under 860px the sidebar
 * hides and a hamburger opens the same navigation in a drawer.
 */
export default function AppLayout() {
  const { user, can, storage: storageInfo } = useAuth();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  const { data: departments } = useAsync(() => adminService.listDepartments(), []);
  const { data: tree } = useAsync(() => folderService.tree(), []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Department visibility: non-admins see their own department and any department that
  // shared with them at full strength; others are shown at 50% opacity (v2 spec).
  const visibleDepartments = useMemo(() => {
    const list = (departments || []).filter((d) => d.status === 'active');
    const seeAll = can(P.ADMIN_DOCUMENT_OVERRIDE) || can(P.ADMIN_DEPARTMENT_MANAGE);
    return list.map((d) => ({ ...d, isVisible: seeAll || d.id === user?.department?.id || d.isMine }));
  }, [departments, can, user]);

  // Sub-units = top-level child folders of each department root.
  const subunitsByDept = useMemo(() => {
    const map = {};
    if (!tree || !departments) return map;
    departments.forEach((d) => {
      if (!d.rootFolderId) return;
      map[d.id] = tree
        .filter((f) => f.parentId === d.rootFolderId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, 6);
    });
    return map;
  }, [tree, departments]);

  // `total` was once `5 * ONE_TB` — a quota nobody set and nothing enforced, which the sidebar
  // drew a bar against. The allowance is now a real setting and is checked on upload, so the
  // bar is back, and both numbers come from the server rather than being summed here: the
  // figure the bar shows and the figure that refuses an upload have to be the same one, and
  // the department sum is only the departments this person can see.
  const storage = storageInfo?.driver
    ? {
        ...storageInfo,
        used: storageInfo.usedBytes ?? departments?.reduce((sum, d) => sum + (d.storageBytes || 0), 0) ?? 0,
      }
    : null;

  const navProps = { departments: visibleDepartments, subunitsByDept, storage };

  return (
    <div className="shell">
      <Sidebar {...navProps} />
      <MobileDrawer open={navOpen} onClose={() => setNavOpen(false)} {...navProps} />

      <div className="shell__main">
        <Topbar onOpenNav={() => setNavOpen(true)} />
        <main className="main shell__content" id="main-content">
          <Outlet />
        </main>
      </div>

      <UploadTray />
    </div>
  );
}
